import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import api from '../utils/api';
import { mapBackendProperties } from '../utils/propertyMapper';
import { useAuth } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import { getSavedPropertiesFromAPI, toggleSavedPropertyAPI } from '../utils/savedProperties';

const Properties = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { formatPrice, language } = useLanguageCurrency();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [propertyStatus, setPropertyStatus] = useState('rent'); // 'rent' or 'sale'
  const [selectedTypes, setSelectedTypes] = useState([]); // Array of selected types
  const [selectedCategory, setSelectedCategory] = useState(''); // 'compound' or 'separate'
  const [priceRange, setPriceRange] = useState({ min: 1000, max: 500000 });
  const [bedrooms, setBedrooms] = useState('');
  const [baths, setBaths] = useState('');
  const [kitchenType, setKitchenType] = useState(''); // 'full' or 'corner'
  const [location, setLocation] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false); // Mobile filter toggle
  const [copiedPropertyId, setCopiedPropertyId] = useState(null);
  const [savedProperties, setSavedProperties] = useState(new Set());
  const [hoveredVideoId, setHoveredVideoId] = useState(null);
  const videoRefs = useRef({});
  const propertiesPerPage = 6;

  // Explicitly play/pause hover video so it doesn't get stuck
  useEffect(() => {
    Object.keys(videoRefs.current).forEach((id) => {
      const v = videoRefs.current[id];
      if (!v) return;
      if (id === hoveredVideoId) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        v.pause();
        v.currentTime = 0;
      }
    });
  }, [hoveredVideoId]);

  useEffect(() => {
    fetchProperties();
  }, [routeLocation.pathname]);

  useEffect(() => {
    filterProperties();
    setCurrentPage(1); // Reset to first page when filters change
  }, [properties, searchTerm, propertyStatus, selectedTypes, selectedCategory, priceRange, bedrooms, baths, kitchenType, location, sortBy]);

  // Load saved property IDs from backend when user is client
  useEffect(() => {
    if (!user) {
      setSavedProperties(new Set());
      return;
    }
    let cancelled = false;
    getSavedPropertiesFromAPI()
      .then(({ savedPropertyIds }) => {
        if (!cancelled) setSavedProperties(new Set((savedPropertyIds || []).map(String)));
      })
      .catch(() => { if (!cancelled) setSavedProperties(new Set()); });
    return () => { cancelled = true; };
  }, [user]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/properties', { params: { limit: 200 } });
      const rawList = response.data?.properties ?? response.data ?? [];
      const list = mapBackendProperties(Array.isArray(rawList) ? rawList : [], language?.slice(0, 2) || 'en');
      setProperties(list);
      setFilteredProperties(list);
    } catch (apiError) {
      console.error('Error fetching properties:', apiError);
      setProperties([]);
      setFilteredProperties([]);
    } finally {
      setLoading(false);
    }
  };


  const filterProperties = () => {
    let filtered = [...properties];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (property) =>
          (typeof property.title === 'string' && property.title.toLowerCase().includes(term)) ||
          (typeof property.location === 'string' && property.location.toLowerCase().includes(term)) ||
          (typeof property.address === 'string' && property.address.toLowerCase().includes(term)) ||
          (property.code && typeof property.code === 'string' && property.code.toLowerCase().includes(term))
      );
    }

    // Property Status filter (For Rent/For Sale)
    // Since all properties are for rent, we'll keep this for future use
    // if (propertyStatus === 'sale') {
    //   filtered = filtered.filter((property) => property.status === 'sale');
    // }

    // Type filter (multiple selection)
    if (selectedTypes.length > 0) {
      filtered = filtered.filter((property) => selectedTypes.includes(property.type));
    }

    // Category filter (compound or separate)
    if (selectedCategory) {
      filtered = filtered.filter((property) => property.category === selectedCategory);
    }

    // Price filter
    if (priceRange.min !== undefined) {
      filtered = filtered.filter((property) => property.price >= priceRange.min);
    }
    if (priceRange.max !== undefined) {
      filtered = filtered.filter((property) => property.price <= priceRange.max);
    }

    // Bedrooms filter
    if (bedrooms) {
      filtered = filtered.filter((property) => property.bedrooms === parseInt(bedrooms));
    }

    // Bathrooms filter
    if (baths) {
      filtered = filtered.filter((property) => property.bathrooms === parseInt(baths));
    }

    // Kitchen Type filter
    if (kitchenType) {
      filtered = filtered.filter((property) => property.kitchenType === kitchenType);
    }

    // Location filter
    if (location) {
      const loc = location.toLowerCase();
      filtered = filtered.filter((property) => 
        (typeof property.location === 'string' && property.location.toLowerCase().includes(loc)) ||
        (typeof property.address === 'string' && property.address.toLowerCase().includes(loc))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        default:
          return b.id - a.id;
      }
    });

    setFilteredProperties(filtered);
  };

  const handleTypeToggle = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleBookNow = (propertyId) => {
    navigate(`/book/${propertyId}`);
  };

  const handlePropertyClick = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  const handleShare = async (propertyId, e) => {
    e.stopPropagation();
    const property = properties.find(p => p.id === propertyId) || filteredProperties.find(p => p.id === propertyId);
    const propertyUrl = `${window.location.origin}/properties/${propertyId}`;
    const shareData = {
      title: property ? property.title : t('common.property'),
      text: property ? `${t('common.shareProperty')}: ${property.title}` : t('common.shareProperty'),
      url: propertyUrl,
    };

    try {
      // Check if Web Share API is available
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(propertyUrl);
        setCopiedPropertyId(propertyId);
        setTimeout(() => {
          setCopiedPropertyId(null);
        }, 2000);
      }
    } catch (err) {
      // User cancelled or error occurred
      if (err.name !== 'AbortError') {
        // If not user cancellation, try clipboard fallback
        try {
          await navigator.clipboard.writeText(propertyUrl);
          setCopiedPropertyId(propertyId);
          setTimeout(() => {
            setCopiedPropertyId(null);
          }, 2000);
        } catch (clipboardErr) {
          console.error('Failed to share:', clipboardErr);
        }
      }
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to the properties section
    const propertiesSection = document.getElementById('properties-section');
    if (propertiesSection) {
      propertiesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback to top if section not found
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-300">{t('properties.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(180deg, rgba(29, 42, 59, 0.98) 0%, rgba(29, 42, 59, 0.96) 50%, rgba(29, 42, 59, 0.95) 100%)',
          width: '100%',
          minHeight: 'auto',
          position: 'relative'
        }}
      >
        {/* Subtle Gold Glow at Top */}
        <div className="absolute top-0 left-0 right-0 h-1/2 opacity-20" style={{
          background: 'linear-gradient(180deg, rgba(213, 181, 119, 0.1) 0%, transparent 100%)',
          pointerEvents: 'none'
        }}></div>
        
        {/* Subtle Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.3) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
          filter: 'blur(60px)'
        }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-5" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
          filter: 'blur(60px)'
        }}></div>

        <div className="w-full max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-1 mx-auto mb-3 rounded-full animate-line-expand" style={{ 
                background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
              }}></div>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight relative flex items-center justify-center flex-wrap gap-2 sm:gap-4" style={{ 
              textShadow: '0 0 30px rgba(213, 181, 119, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3)',
              fontSize: 'clamp(3.5rem, 7vw, 6rem)'
            }}>
              <span className="relative inline-flex items-center justify-center gap-2 sm:gap-4">
                {/* Home Icon */}
                <svg 
                  className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 animate-icon-float"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    color: '#D5B577',
                    filter: 'drop-shadow(0 0 10px rgba(213, 181, 119, 0.5))'
                  }}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                  />
                </svg>
                {/* Animated Text */}
                <span className="all-properties-text" style={{ fontSize: 'inherit' }}>
                  {language === 'ar' ? (
                    <span
                      style={{
                        background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                        backgroundSize: '200% 200%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'gradient-shift-once 3s ease-in-out forwards',
                        fontSize: 'inherit'
                      }}
                    >
                      {t('properties.title')}
                    </span>
                  ) : (
                    t('properties.title').split('').map((char, index) => (
                      <span
                        key={index}
                        className="inline-block"
                        style={{
                          animationDelay: `${index * 0.08}s`,
                          background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                          backgroundSize: '200% 200%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'gradient-shift-once 3s ease-in-out forwards',
                          fontSize: 'inherit'
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))
                  )}
                </span>
                {/* Animated underline */}
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 animate-underline-expand" style={{
                  background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                  opacity: 0.8,
                  minWidth: '100%'
                }}></div>
                {/* Glow effect */}
                <div className="absolute inset-0 animate-pulse-glow" style={{
                  background: 'radial-gradient(circle, rgba(213, 181, 119, 0.3) 0%, rgba(213, 181, 119, 0.15) 50%, transparent 70%)',
                  filter: 'blur(20px)',
                  zIndex: -1
                }}></div>
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed font-light animate-fade-in-up" style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              animationDelay: '0.8s',
              animationFillMode: 'both'
            }}>
              {t('properties.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section id="properties-section" className="py-16 px-4 sm:px-6 lg:px-8 relative" style={{ 
        backgroundColor: '#1D2A3B', 
        width: '100%', 
        minHeight: 'auto'
      }}>
        <div className="w-full max-w-7xl mx-auto">
          {/* Search Bar - Prominent Design */}
          <div className="max-w-4xl mx-auto lg:mx-0 lg:max-w-full mb-12">
            <div 
              className="relative p-5 rounded-2xl transition-all duration-300"
              style={{
                background: 'rgba(29, 42, 59, 0.85)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(213, 181, 119, 0.25)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(213, 181, 119, 0.15), inset 0 0 30px rgba(213, 181, 119, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.4)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.25), inset 0 0 40px rgba(213, 181, 119, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.25)';
                e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(213, 181, 119, 0.15), inset 0 0 30px rgba(213, 181, 119, 0.08)';
              }}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none z-10">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={t('properties.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-14 pr-5 py-4 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D5B577] focus:border-[#D5B577] text-white placeholder-gray-300 text-lg transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Mobile Filter Toggle Button */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-white rounded-xl shadow-lg font-semibold transition-all hover:shadow-xl"
              style={{ color: '#1D2A3B' }}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {t('properties.filters')}
              </span>
              <svg
                className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            {/* Left Sidebar - Filters */}
            <aside className={`w-full lg:w-80 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6 sticky top-24" style={{ maxHeight: 'calc(100vh - 6rem)', overflowY: 'auto', overflowX: 'hidden' }}>
                {/* Category */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.category')}</h3>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value="compound"
                        checked={selectedCategory === 'compound'}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-5 h-5 text-[#D5B577] focus:ring-[#D5B577] focus:ring-2 border-gray-300"
                      />
                      <span className="ml-3 text-gray-700 text-sm">{t('properties.inCompound')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value="separate"
                        checked={selectedCategory === 'separate'}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-5 h-5 text-[#D5B577] focus:ring-[#D5B577] focus:ring-2 border-gray-300"
                      />
                      <span className="ml-3 text-gray-700 text-sm">{t('properties.separate')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value=""
                        checked={selectedCategory === ''}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-5 h-5 text-[#D5B577] focus:ring-[#D5B577] focus:ring-2 border-gray-300"
                      />
                      <span className="ml-3 text-gray-700 text-sm">{t('properties.all')}</span>
                    </label>
                  </div>
                </div>

                {/* Property Type */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.propertyType')}</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {[
                      { key: 'apartment', value: 'Apartment' },
                      { key: 'villa', value: 'Villa' },
                      { key: 'studio', value: 'Studio' },
                      { key: 'vacationHome', value: 'Vacation Home' },
                      { key: 'house', value: 'House' },
                      { key: 'townhome', value: 'Townhome' }
                    ].map(({ key, value }) => (
                      <label key={value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(value)}
                          onChange={() => handleTypeToggle(value)}
                          className="w-5 h-5 text-[#D5B577] focus:ring-[#D5B577] focus:ring-2 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-gray-700 text-sm">{t(`properties.propertyTypes.${key}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.priceRange')}</h3>
                  <div className="mb-4 relative" style={{ height: '40px', paddingTop: '15px' }}>
                    <div className="absolute inset-0 flex items-center pt-4">
                      <div className="h-2 bg-gray-200 rounded-lg w-full relative">
                        <div 
                          className="absolute h-2 rounded-lg"
                          style={{
                            left: `${((priceRange.min - 1000) / 499000) * 100}%`,
                            width: `${((priceRange.max - priceRange.min) / 499000) * 100}%`,
                            background: '#D5B577'
                          }}
                        />
                      </div>
                    </div>
                    <input
                      type="range"
                      min="1000"
                      max="500000"
                      value={priceRange.min}
                      onChange={(e) => {
                        const newMin = parseInt(e.target.value);
                        if (newMin <= priceRange.max) {
                          setPriceRange({ ...priceRange, min: newMin });
                        }
                      }}
                      className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer"
                      style={{ 
                        zIndex: priceRange.min > priceRange.max - 20 ? 20 : 10,
                        top: '15px'
                      }}
                    />
                    <input
                      type="range"
                      min="1000"
                      max="500000"
                      value={priceRange.max}
                      onChange={(e) => {
                        const newMax = parseInt(e.target.value);
                        if (newMax >= priceRange.min) {
                          setPriceRange({ ...priceRange, max: newMax });
                        }
                      }}
                      className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer"
                      style={{ 
                        zIndex: priceRange.min > priceRange.max - 20 ? 10 : 20,
                        top: '15px'
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <label className="block text-sm text-gray-600 mb-1">{t('properties.min')}</label>
                      <input
                        type="number"
                        value={priceRange.min}
                        onChange={(e) => {
                          const newMin = parseInt(e.target.value) || 0;
                          if (newMin <= priceRange.max && newMin >= 1000) {
                            setPriceRange({ ...priceRange, min: newMin });
                          }
                        }}
                        className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] text-gray-900 text-sm"
                        style={{ width: '262px', height: '42px' }}
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm text-gray-600 mb-1">{t('properties.max')}</label>
                      <input
                        type="number"
                        value={priceRange.max}
                        onChange={(e) => {
                          const newMax = parseInt(e.target.value) || 1000;
                          if (newMax >= priceRange.min && newMax <= 500000) {
                            setPriceRange({ ...priceRange, max: newMax });
                          }
                        }}
                        className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] text-gray-900 text-sm"
                        style={{ width: '262px', height: '42px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Bedrooms */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.bedrooms')}</h3>
                  <div className="relative">
                    <select
                      value={bedrooms}
                      onChange={(e) => setBedrooms(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none text-gray-900 text-sm"
                    >
                      <option value="">{t('properties.bedrooms')} : {t('common.any')}</option>
                      <option value="1">1 {t('properties.bedrooms')}</option>
                      <option value="2">2 {t('properties.bedrooms')}</option>
                      <option value="3">3 {t('properties.bedrooms')}</option>
                      <option value="4">4 {t('properties.bedrooms')}</option>
                      <option value="5">5+ {t('properties.bedrooms')}</option>
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Baths */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.baths')}</h3>
                  <div className="relative">
                    <select
                      value={baths}
                      onChange={(e) => setBaths(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none text-gray-900 text-sm"
                    >
                      <option value="">{t('properties.bathsAny')}</option>
                      <option value="1">1 {t('properties.baths')}</option>
                      <option value="2">2 {t('properties.baths')}</option>
                      <option value="3">3 {t('properties.baths')}</option>
                      <option value="4">4+ {t('properties.baths')}</option>
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Kitchen Type */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.kitchen')}</h3>
                  <div className="relative">
                    <select
                      value={kitchenType}
                      onChange={(e) => setKitchenType(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none text-gray-900 text-sm"
                    >
                      <option value="">{t('properties.kitchenAny')}</option>
                      <option value="full">{t('properties.fullKitchen')}</option>
                      <option value="corner">{t('properties.cornerKitchen')}</option>
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Location */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{t('properties.location')}</h3>
                  <div className="relative">
                    <select
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none text-gray-900 text-sm"
                    >
                      <option value="">{t('properties.filterByCountry')}</option>
                      <option value="cairo">{t('common.locations.cairo')}</option>
                      <option value="alexandria">{t('common.locations.alexandria')}</option>
                      <option value="giza">{t('common.locations.giza')}</option>
                      <option value="sharm-el-sheikh">{t('common.locations.sharmElSheikh')}</option>
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </aside>

            {/* Right Main Area - Properties Grid */}
            <div className="flex-1">
              {/* Sort Dropdown - Aligned with Filter Box */}
              <div className="flex justify-end mb-8 lg:sticky lg:top-24 lg:z-10 lg:mb-0 lg:pb-6">
                <div className="relative w-full sm:w-auto">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5B577] appearance-none text-sm pr-8"
                    style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(213, 181, 119, 0.3)',
                      color: '#FFFFFF'
                    }}
                  >
                    <option value="newest" style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>{t('properties.sortBy')}: {t('properties.default')}</option>
                    <option value="price-low" style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>{t('properties.sortOptions.priceLow')}</option>
                    <option value="price-high" style={{ backgroundColor: '#1D2A3B', color: '#FFFFFF' }}>{t('properties.sortOptions.priceHigh')}</option>
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {properties.length === 0 ? (
            /* No active properties: show static "Coming Soon" placeholder cards */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={`coming-soon-${i}`}
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(213, 181, 119, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.1)',
                    cursor: 'default',
                    pointerEvents: 'none',
                  }}
                >
                  <div className="relative h-56 overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.95) 0%, rgba(45, 55, 75, 0.9) 50%, rgba(29, 42, 59, 0.95) 100%)' }}>
                    <svg className="w-16 h-16 opacity-30" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span
                      className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.4) 0%, rgba(213, 181, 119, 0.25) 100%)',
                        color: '#D5B577',
                        border: '1px solid rgba(213, 181, 119, 0.5)',
                      }}
                    >
                      {t('properties.comingSoon')}
                    </span>
                  </div>
                  <div className="p-5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="text-lg font-bold mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {t('properties.comingSoon')}
                    </h3>
                    <p className="text-sm mb-4 flex items-center gap-1" style={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      —
                    </p>
                    <div className="flex items-center gap-4 text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                      <span className="flex items-center gap-1">— {t('home.bedrooms')}</span>
                      <span className="flex items-center gap-1">— {t('home.bathrooms')}</span>
                      <span className="flex items-center gap-1">— m²</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-20">
              <svg
                className="mx-auto h-24 w-24 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-2xl font-bold text-white mb-2">{t('properties.noProperties')}</h3>
              <p className="text-gray-300 mb-6">{t('properties.tryAdjustingFilters')}</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setPropertyStatus('rent');
                  setSelectedTypes([]);
                  setSelectedCategory('');
                  setPriceRange({ min: 1000, max: 3000 });
                  setBedrooms('');
                  setBaths('');
                  setKitchenType('');
                  setLocation('');
                }}
                className="px-6 py-3 bg-[#D5B577] hover:bg-[#c4a366] text-white font-semibold rounded-xl transition-colors"
              >
                {t('properties.clearFilters')}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {currentProperties.map((property) => (
                  <div
                    key={property.id}
                    onClick={() => handlePropertyClick(property.id)}
                    className="rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                    style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(213, 181, 119, 0.2)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.1)',
                      transform: 'translateY(0) translateZ(0)',
                      transition: 'all 0.3s ease',
                      willChange: 'transform',
                      WebkitTransform: 'translateY(0) translateZ(0)'
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget;
                      el.style.cssText += '; transform: translateY(-8px) translateZ(0) !important; -webkit-transform: translateY(-8px) translateZ(0) !important;';
                      el.style.borderColor = 'rgba(213, 181, 119, 0.4)';
                      el.style.boxShadow = '0 12px 40px rgba(213, 181, 119, 0.2), 0 0 0 1px rgba(213, 181, 119, 0.3)';
                      el.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget;
                      el.style.cssText += '; transform: translateY(0) translateZ(0) !important; -webkit-transform: translateY(0) translateZ(0) !important;';
                      el.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                      el.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.1)';
                      el.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }}
                  >
                    <div
                      className="relative h-56 overflow-hidden group"
                      onMouseEnter={() => property.video && setHoveredVideoId(property.id)}
                      onMouseLeave={() => setHoveredVideoId(null)}
                    >
                      <img
                        src={property.image}
                        alt={property.title}
                        className={`w-full h-full object-cover transition-transform duration-500 transition-opacity duration-200 group-hover:scale-110 ${hoveredVideoId === property.id ? 'opacity-0 absolute inset-0' : 'opacity-100'}`}
                        loading="lazy"
                      />
                      {property.video && (
                        <div
                          className={`absolute inset-0 z-10 bg-black transition-opacity duration-200 ${hoveredVideoId === property.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
                        >
                          <video
                            ref={(el) => {
                              if (el) videoRefs.current[property.id] = el;
                              else delete videoRefs.current[property.id];
                            }}
                            src={typeof property.video === 'string' ? property.video : (property.videos && property.videos[0]) || ''}
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {property.video && hoveredVideoId !== property.id && (
                        <div className="absolute bottom-3 right-3 z-10">
                          <div className="w-8 h-8 rounded-full bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white border-opacity-20"
                            style={{ 
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              backdropFilter: 'blur(8px)'
                            }}
                          >
                            <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      )}
                      {property.featured && (
                        <span 
                          className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg"
                          style={{ 
                            background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
                            color: '#1D2A3B',
                            border: '1px solid rgba(213, 181, 119, 0.5)'
                          }}
                        >
                          {t('properties.featured')}
                        </span>
                      )}
                      <span 
                        className="absolute bottom-3 left-3 text-sm font-semibold px-4 py-2 rounded-lg shadow-xl"
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.95) 0%, rgba(29, 42, 59, 0.85) 100%)',
                          color: '#D5B577',
                          border: '1px solid rgba(213, 181, 119, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        {formatPrice(property.price, false)} {t('properties.perNight')}
                      </span>
                    </div>

                    <div className="p-5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="text-lg font-bold" style={{ color: '#FFFFFF' }}>
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-2">
                          {property.code && (
                            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577' }}>
                              {property.code}
                            </span>
                          )}
                          {property.category && (
                            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.3)' }}>
                              {property.category === 'compound' ? t('properties.inCompound') : t('properties.separate')}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm mb-4 flex items-center gap-1" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        {property.address || property.location}
                      </p>
                      <div className="flex items-center gap-4 text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          {property.bedrooms} {t('home.bedrooms')}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                          </svg>
                          {property.bathrooms} {t('home.bathrooms')}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                          {property.sqft ? Math.round(property.sqft * 0.092903) : 'N/A'} m²
                        </span>
                      </div>
                      <div className="flex items-center justify-end">
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => handleShare(property.id, e)}
                            className="p-2 transition-colors relative rounded-lg"
                            style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#D5B577';
                              e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            aria-label="Share"
                          >
                            {copiedPropertyId === property.id ? (
                              <span 
                                className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10"
                                style={{ 
                                  backgroundColor: 'rgba(29, 42, 59, 0.95)',
                                  border: '1px solid rgba(213, 181, 119, 0.3)',
                                  backdropFilter: 'blur(10px)'
                                }}
                              >
                                {t('properties.copied')}
                              </span>
                            ) : null}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (user) {
                                const id = String(property.id ?? property._id ?? '');
                                const currentlySaved = savedProperties.has(id);
                                toggleSavedPropertyAPI(id, currentlySaved)
                                  .then((newSaved) => {
                                    setSavedProperties(prev => {
                                      const next = new Set(prev);
                                      if (newSaved) next.add(id); else next.delete(id);
                                      return next;
                                    });
                                  })
                                  .catch(() => {});
                              } else {
                                navigate('/login', { state: { redirectTo: '/client', redirectView: 'saved' } });
                              }
                            }}
                            className="p-2 transition-colors rounded-lg"
                            style={{ 
                              color: user && savedProperties.has(String(property.id ?? property._id ?? '')) 
                                ? '#ef4444' 
                                : 'rgba(255, 255, 255, 0.5)' 
                            }}
                            onMouseEnter={(e) => {
                              if (!user || !savedProperties.has(String(property.id ?? property._id ?? ''))) {
                                e.currentTarget.style.color = '#ef4444';
                                e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!user || !savedProperties.has(String(property.id ?? property._id ?? ''))) {
                                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)';
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                            aria-label={user && savedProperties.has(String(property.id ?? property._id ?? '')) ? 'Unlike' : 'Like'}
                          >
                            <svg 
                              className="w-4 h-4" 
                              fill={user && savedProperties.has(String(property.id ?? property._id ?? '')) ? 'currentColor' : 'none'} 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center items-center gap-2 p-4 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(213, 181, 119, 0.2)' }}>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: currentPage === 1 ? 'rgba(29, 42, 59, 0.5)' : 'rgba(255, 255, 255, 0.05)',
                      color: '#D5B577',
                      border: '1px solid rgba(213, 181, 119, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                      }
                    }}
                  >
                    {t('properties.previous')}
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => paginate(pageNumber)}
                          className="px-4 py-2 rounded-lg font-semibold transition-all duration-200"
                          style={{ 
                            backgroundColor: currentPage === pageNumber ? '#D5B577' : 'rgba(255, 255, 255, 0.05)',
                            color: currentPage === pageNumber ? '#1D2A3B' : '#D5B577',
                            border: '1px solid rgba(213, 181, 119, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage !== pageNumber) {
                              e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.2)';
                              e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentPage !== pageNumber) {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                              e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                            }
                          }}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber} className="text-gray-400">...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: currentPage === totalPages ? 'rgba(29, 42, 59, 0.5)' : '#1D2A3B',
                      color: '#D5B577',
                      border: '1px solid rgba(213, 181, 119, 0.3)'
                    }}
                  >
                    {t('properties.next')}
                  </button>
                </div>
              )}
            </>
          )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* WhatsApp Button */}
      <WhatsAppButton phoneNumber="+1234567890" message="Hello! I'm interested in viewing your properties." />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { 
            background-position: 0% 50%;
            transform: translateY(0);
          }
          50% { 
            background-position: 100% 50%;
            transform: translateY(-3px);
          }
        }
        
        @keyframes gradient-shift-once {
          0% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 0;
          }
          50% { 
            background-position: 100% 50%;
            transform: translateY(-3px);
            opacity: 1;
          }
          100% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes underline-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0.8;
          }
          100% { 
            width: 100%;
            opacity: 0.8;
          }
        }
        
        .animate-underline-expand {
          animation: underline-expand 1.2s ease-out 0.5s forwards;
          width: 100%;
          opacity: 0.8;
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 0.6;
            transform: scale(1.1);
          }
        }
        
        @keyframes line-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes line-expand-delayed {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes fade-in-up {
          0% { 
            opacity: 0;
            transform: translateY(20px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes icon-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          50% { 
            transform: translateY(-8px) rotate(5deg);
          }
        }
        
        .all-properties-text span {
          animation: letter-float-once 2s ease-out forwards;
        }
        
        @keyframes letter-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          50% { 
            transform: translateY(-4px) rotate(1deg);
          }
        }
        
        @keyframes letter-float-once {
          0% { 
            transform: translateY(20px) rotate(0deg);
            opacity: 0;
          }
          100% { 
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
        }
        
        .animate-line-expand {
          animation: line-expand 1.5s ease-out forwards;
        }
        
        .animate-underline-expand {
          animation: underline-expand 1.2s ease-out 0.5s forwards;
        }
        
        .animate-underline-expand {
          animation-fill-mode: forwards;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        
        .animate-line-expand-delayed {
          animation: line-expand-delayed 1.5s ease-out 1s forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-icon-float {
          animation: icon-float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Properties;
